import { useCallback } from 'react'
import useSWR from 'swr'
import { ThreadArtifactListResponse } from '@digital-twins/shared-types'

import { knowledgeThreadsApi } from '@/lib/api'

export interface UseThreadLivingArtifactsResult {
  artifacts: ThreadArtifactListResponse['artifacts']
  isLoading: boolean
  error: unknown
  refresh: () => Promise<ThreadArtifactListResponse | undefined>
}

/**
 * Living thread library for the artifacts rail.
 * Source: `GET /knowledge/threads/:token/artifacts` — not the workflow run.
 */
export function useThreadLivingArtifacts(
  threadToken: string | null | undefined,
): UseThreadLivingArtifactsResult {
  const swrKey = threadToken
    ? (['thread-living-artifacts', threadToken] as const)
    : null

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    ([, token]: readonly ['thread-living-artifacts', string]) =>
      knowledgeThreadsApi.listArtifacts(token),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  const refresh = useCallback(async () => {
    return mutate()
  }, [mutate])

  return {
    artifacts: data?.artifacts ?? [],
    isLoading: Boolean(threadToken) && isLoading,
    error,
    refresh,
  }
}
